import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

type Order = 'asc' | 'desc';

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  format?: (value: any) => string | React.ReactNode;
}

interface Action {
  label: string;
  icon?: React.ReactNode;
  onClick: (row: any) => void;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'inherit';
}

interface DataTableProps {
  columns: Column[];
  rows: any[];
  title?: string;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  onView?: (row: any) => void;
  actions?: Action[];
  initialSortBy?: string;
  initialOrder?: Order;
  rowsPerPageOptions?: number[];
  loading?: boolean;
  /** Persistiert Filter/Seite/Sortierung in sessionStorage (überlebt Detail→Zurück) */
  persistKey?: string;
}

type PersistedTableState = {
  page: number;
  rowsPerPage: number;
  orderBy: string;
  order: Order;
  filterText: string;
};

const loadPersistedState = (
  persistKey: string | undefined,
  defaults: PersistedTableState
): PersistedTableState => {
  if (!persistKey) return defaults;
  try {
    const raw = sessionStorage.getItem(`datatable:${persistKey}`);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<PersistedTableState>;
    return {
      page: typeof parsed.page === 'number' ? parsed.page : defaults.page,
      rowsPerPage:
        typeof parsed.rowsPerPage === 'number' ? parsed.rowsPerPage : defaults.rowsPerPage,
      orderBy: typeof parsed.orderBy === 'string' ? parsed.orderBy : defaults.orderBy,
      order: parsed.order === 'asc' || parsed.order === 'desc' ? parsed.order : defaults.order,
      filterText: typeof parsed.filterText === 'string' ? parsed.filterText : defaults.filterText,
    };
  } catch {
    return defaults;
  }
};

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  title,
  onEdit,
  onDelete,
  onView,
  actions = [],
  initialSortBy = columns[0]?.id,
  initialOrder = 'asc',
  rowsPerPageOptions = [10, 25, 50],
  loading = false,
  persistKey,
}) => {
  const theme = useTheme();
  const defaults: PersistedTableState = {
    page: 0,
    rowsPerPage: rowsPerPageOptions[0],
    orderBy: initialSortBy,
    order: initialOrder,
    filterText: '',
  };
  const initial = loadPersistedState(persistKey, defaults);

  const [page, setPage] = useState(initial.page);
  const [rowsPerPage, setRowsPerPage] = useState(initial.rowsPerPage);
  const [orderBy, setOrderBy] = useState<string>(initial.orderBy);
  const [order, setOrder] = useState<Order>(initial.order);
  const [filterText, setFilterText] = useState(initial.filterText);

  useEffect(() => {
    if (!persistKey) return;
    const state: PersistedTableState = { page, rowsPerPage, orderBy, order, filterText };
    sessionStorage.setItem(`datatable:${persistKey}`, JSON.stringify(state));
  }, [persistKey, page, rowsPerPage, orderBy, order, filterText]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    setPage(0);
  };

  const filteredRows = (rows ?? []).filter((row) =>
    Object.values(row).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(filterText.toLowerCase())
    )
  );

  // Seite zurücksetzen, falls Filter/Daten weniger Seiten ergeben
  const maxPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);

  const sortedRows = [...filteredRows].sort((a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (!aValue) return order === 'asc' ? -1 : 1;
    if (!bValue) return order === 'asc' ? 1 : -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return order === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return order === 'asc'
      ? aValue < bValue
        ? -1
        : 1
      : bValue < aValue
      ? -1
      : 1;
  });

  const paginatedRows = sortedRows.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage
  );

  return (
    <Paper
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: 2,
        boxShadow: theme.shadows[2],
      }}
    >
      <Box sx={{ p: 2 }}>
        {title && (
          <Typography variant="h6" component="h2" gutterBottom>
            {title}
          </Typography>
        )}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Suchen..."
          value={filterText}
          onChange={handleFilterChange}
          size="small"
          sx={{ mb: 2 }}
        />
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                {(onView || onEdit || onDelete || actions.length > 0) && (
                  <TableCell align="right">Aktionen</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((row, index) => (
                <TableRow
                  hover
                  key={row.id ?? index}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    cursor: onView ? 'pointer' : 'default',
                  }}
                  onClick={onView ? () => onView(row) : undefined}
                >
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      {column.format
                        ? column.format(row[column.id])
                        : row[column.id]}
                    </TableCell>
                  ))}
                  {(onView || onEdit || onDelete || actions.length > 0) && (
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        {onView && (
                          <Tooltip title="Anzeigen">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onView(row);
                              }}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onEdit && (
                          <Tooltip title="Bearbeiten">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(row);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDelete && (
                          <Tooltip title="Löschen">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(row);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {actions.map((action, actionIndex) => (
                          <Tooltip key={actionIndex} title={action.label}>
                            <IconButton
                              size="small"
                              color={action.color || 'primary'}
                              onClick={(e) => {
                                e.stopPropagation();
                                action.onClick(row);
                              }}
                            >
                              {action.icon}
                            </IconButton>
                          </Tooltip>
                        ))}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={safePage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} von ${count}`
          }
        />
      </Box>
    </Paper>
  );
};
